import React, {useEffect, useId, useState} from 'react';
import parseHtml from 'html-react-parser';
import {useTranslation} from 'react-i18next';
import {Alert, Checkbox, Flex, Space} from 'antd';
import {Attachments} from '@ant-design/x';
import {CheckIcon, CloseIcon, Download2Icon} from '../../icons';
import {colorsStyles} from '../../utils/styles';
import {
  StyledAgentChatSharedConfirmationOption as ConfirmationOption,
  StyledAgentChatSharedOptionButton as OptionButton,
  StyledAgentChatSharedReport,
  StyledAgentChatSharedReportNote,
  StyledAgentChatSharedTable,
  StyledAgentChatSharedDownloadButton,
} from './StyledAgentChatShared';
import IconWrapper from '../IconWrapper';
import {CONTENT_TYPE, REPORT_ACTION, REPORT_ANGLE_PATTERN, REPORT_DOWNLOAD_ACTION_TYPES} from '../../constants/agentChat.constants';
import {sanitizeReportHtml} from '../../utils/helpers/sanitize.helpers';
import {getReportExport, getReportExportExpiry, printReport} from '../../utils/helpers/report.helpers';

// The longest delay setTimeout honours; a longer one fires at once. A link expiring later is re-checked after this long.
const MAX_TIMER_DELAY_MS = 2 ** 31 - 1;

// e.g. "text/csv" -> "CSV", "application/vnd.ms-excel" -> "MS-EXCEL"
const getAttachmentFormatLabel = (mimeType) => {
  const subtype = mimeType?.split('/')[1];
  return subtype ? subtype.split('+')[0].replace(/^x-/, '').toUpperCase() : null;
};

// `parseMessage` splits a message around any markdown table it contains so a real table
// component can be rendered in place of raw <table> markup; this reassembles the pieces in order.
const renderParsedMessage = (parsed, attachment) => (
  <>
    {parsed.before}
    {parsed.table && <AgentChatMessageTable table={parsed.table} attachment={attachment} />}
    {parsed.after}
  </>
);

const AgentChatMessageTable = ({ table, attachment }) => {
  const [t] = useTranslation('agentChat');

  const formatLabel = getAttachmentFormatLabel(attachment?.mime_type);

  const columns = table.headers.map((header, index) => ({
    title: header,
    dataIndex: String(index),
    key: String(index),
  }));
  const dataSource = table.rows.map((row, rowIndex) => ({
    key: rowIndex,
    ...row.reduce((acc, cell, cellIndex) => ({ ...acc, [String(cellIndex)]: cell }), {}),
  }));

  return (
    <StyledAgentChatSharedTable
      size='small'
      pagination={false}
      columns={columns}
      dataSource={dataSource}
      locale={{ emptyText: t('table.noResults') }}
      footer={() => (
        <Flex justify='flex-end'>
          {attachment?.url && (
            <StyledAgentChatSharedDownloadButton
              className='d-flex-child'
              size='small'
              href={attachment.url}
              target='_blank'
              rel='noopener noreferrer'
              icon={<IconWrapper size={11} color={colorsStyles.neutral_8}><Download2Icon /></IconWrapper>}
            >
              {formatLabel ? t('table.download', {format: formatLabel}) : t('table.downloadFile')}
            </StyledAgentChatSharedDownloadButton>
          )}
        </Flex>
      )}
    />
  );
};

const AgentChatOptions = ({
  message,
  options,
  attachment,
  parseMessage,
  onSend,
  selectedOptions,
  onToggleOption,
  onSubmitOptions,
  Confirmation,
}) => {
  const [t] = useTranslation('main');

  const subOptions = typeof options === 'object' && Array.isArray(options.values) ? options.values : [];
  const optionsType = options?.type;
  const isMultiple = optionsType === 'multiple';
  const isOptional = options?.optional || false;
  const fieldName = options?.field;

  return (
    <Confirmation>
      <div className='confirmation-message'>{renderParsedMessage(parseMessage({message, attachment}), attachment)}</div>
      <Space direction='vertical' className='w-100'>
        <Space direction='vertical' className='w-100'>
          {subOptions.map((value) => {
            const valueKey = value.toLowerCase();
            const label = typeof value === 'string' && value.length ? value[0].toUpperCase() + value.slice(1) : value;
            const variant = ['confirm', 'cancel'].includes(valueKey) ? valueKey : 'option';
            return (
              <ConfirmationOption
                variant={variant}
                key={value}
                onClick={() => isMultiple ? onToggleOption(value) : onSend(value)}
              >
                <Space>
                  {['confirm', 'submit'].includes(valueKey) ? <CheckIcon /> : valueKey === 'cancel' ? <CloseIcon /> : null}
                  {optionsType === 'multiple' && (
                    <Checkbox checked={selectedOptions.includes(value)} />
                  )}
                  <span>{label}</span>
                </Space>
              </ConfirmationOption>
            );
          })}
        </Space>

        {(isMultiple || isOptional) && (
          <Space size='middle'>
            {isMultiple && (
              <OptionButton
                onClick={() => onSubmitOptions(selectedOptions)}
                disabled={selectedOptions.length === 0}
                type='link'
              >
                {t('submit')}
              </OptionButton>
            )}
            {isOptional && (
              <OptionButton
                onClick={() => onSend(`Skip ${fieldName}`)}
                type='link'
              >
                {t('skip')}
              </OptionButton>
            )}
          </Space>
        )}
      </Space>
    </Confirmation>
  );
};

/**
 * A server-rendered report, as opposed to a markdown answer.
 *
 * ⚠ SANITISED WITH THE REPORT POLICY, WHICH IS WIDER THAN THE PROSE ONE — the templates carry their
 * whole design as inline CSS and use structural elements the chat never does. It is still untrusted:
 * the template interpolates vendor names, and a crafted one is exactly the payload WIT-222 asserts
 * against server-side. This is the second layer.
 *
 * ⚠ THE REPORT'S OWN `<style>` BLOCK DOES NOT ARRIVE. DOMPurify empties a `<style>` element
 * unconditionally, so the tokens come from `app-desktop`'s stylesheet on the wrapper instead —
 * see `StyledAgentChatSharedReport`, and lib-docs#23 for why the plan now says so too.
 *
 * ⚠ WHAT THIS PATH LOSES IS INVISIBLE WHERE A TEMPLATE IS BUILT. A property off the CSS allowlist, or a
 * token the wrapper does not define, disappears here and nowhere else — the standalone render keeps
 * looking right. Check a template rendered in the app, not only where it was written.
 *
 * ⚠ THE ACTIONS ARE WIRED HERE, BY DELEGATION. The report's buttons arrive as markup carrying only
 * `data-action` — the sanitiser removes anything that could act on its own — so one click handler on
 * the wrapper reads which was pressed. `print` prints this report alone. A download action (`csv`, `xlsx`
 * — `REPORT_DOWNLOAD_ACTION_TYPES`) opens the message's export of its type (written by the server beside
 * the HTML — plan, Decision 17) the way a chat attachment opens.
 *
 * ⚠ A DOWNLOAD THAT CANNOT DOWNLOAD IS DISABLED, AND AN EXPIRED LINK SAYS WHY. Each button is matched to
 * its own export, so one missing or expired export disables only its own button. A missing export is
 * disabled silently: there is nothing the reader could do about it. A link dies an hour after the server
 * minted it (`getReportExportExpiry`), and a report can stay on screen longer; from then on its button is
 * disabled with the reason on hover and written under the report — the plan's interaction state for a
 * failed download. The note sits outside the wrapper, so it never prints. One timer disables each
 * button when its own link expires, and a press reads the clock again, because a timer can fire late
 * after the computer sleeps.
 */
const AgentChatReport = ({html, reportExports, reportId, reportParams, onSend}) => {
  const [t] = useTranslation('agentChat');
  const expiredReasonId = useId();
  const [now, setNow] = useState(Date.now);
  // ⚠ A PIVOT IS SENT ONCE. It asks for this report again by another angle, as a follow-up whose answer is a new
  // report message (WIT-246), so this selector waits disabled from the press on — pressing again would ask twice.
  // Without the answer's own `report_id` there is nothing to ask for, and the selector stays disabled.
  const [pivotSent, setPivotSent] = useState(false);
  const canPivot = Boolean(onSend && reportId) && !pivotSent;

  // Every download a report can offer: the message's export of that type (null when it has none) and when its link dies.
  const downloads = Object.fromEntries(Object.entries(REPORT_DOWNLOAD_ACTION_TYPES).map(([action, mimeType]) => {
    const file = getReportExport(reportExports, mimeType);
    return [action, {file, expiresAt: file ? getReportExportExpiry(file.url) : null}];
  }));
  const isExpired = ({expiresAt}, at = now) => expiresAt !== null && at >= expiresAt;
  const downloadFor = (action) => (Object.prototype.hasOwnProperty.call(downloads, action) ? downloads[action] : null);
  const anyExpired = Object.values(downloads).some((download) => download.file && isExpired(download));
  // The next link still to expire; Infinity when none will.
  const nextExpiry = Math.min(...Object.values(downloads).map(({expiresAt}) => expiresAt).filter((at) => at !== null && at > now));
  const expiredReason = t('report.downloadExpired');

  useEffect(() => {
    if (!Number.isFinite(nextExpiry)) return undefined;
    const timer = setTimeout(() => setNow(Date.now()), Math.min(nextExpiry - Date.now(), MAX_TIMER_DELAY_MS));
    return () => clearTimeout(timer);
  }, [nextExpiry, now]);

  const onClick = (event) => {
    const button = event.target.closest('button[data-action]');
    if (!button || !event.currentTarget.contains(button)) return;
    // ⚠ NOTHING ELSE MAY ALSO HAPPEN ON A HANDLED CLICK. An action's markup is the server's, and an
    // anchor around a report action — or inside one — would keep its own default: the press that
    // says "Download PDF" would print AND navigate this tab wherever that href points, no script
    // involved. The sanitiser drops that pairing too; this is the half that does not depend on
    // markup shape.
    event.preventDefault();
    if (button.dataset.action === REPORT_ACTION.PRINT) printReport(event.currentTarget);
    if (button.dataset.action === REPORT_ACTION.PIVOT) {
      const {angle} = button.dataset;
      // The chosen angle again is a no-op; only the angle comes from the markup, the rest from the answer.
      if (!canPivot || !REPORT_ANGLE_PATTERN.test(angle || '') || button.getAttribute('aria-pressed') === 'true') return;
      setPivotSent(true);
      onSend(button.textContent.trim() || angle, {reportId, reportParams: {...reportParams, angle}});
      return;
    }
    const download = downloadFor(button.dataset.action);
    if (!download?.file || isExpired(download)) return;
    if (isExpired(download, Date.now())) {
      setNow(Date.now());
      return;
    }
    window.open(download.file.url, '_blank', 'noopener,noreferrer');
  };

  // The markup is the server's, so an action that cannot act is disabled on its parsed node, before it becomes an
  // element: disabled, not merely styled dead while still taking a click and the keyboard's focus. A download is
  // unusable when its export is missing or its link expired (only the latter carries a reason); a pivot, when the
  // answer has no report to ask for again or a pivot is already on its way.
  const disableUnusableActions = (node) => {
    if (node.name !== 'button') return;
    if (node.attribs?.['data-action'] === REPORT_ACTION.PIVOT) {
      if (!canPivot) Object.assign(node.attribs, {disabled: ''});
      return;
    }
    const download = downloadFor(node.attribs?.['data-action']);
    if (!download || (download.file && !isExpired(download))) return;
    Object.assign(node.attribs, {disabled: ''});
    if (download.file) {
      Object.assign(node.attribs, {title: expiredReason, 'aria-describedby': expiredReasonId});
    }
  };

  return (
    <>
      <StyledAgentChatSharedReport onClick={onClick}>
        {parseHtml(sanitizeReportHtml(html), {replace: disableUnusableActions})}
      </StyledAgentChatSharedReport>
      {anyExpired && (
        // role=status: the button can disable itself while the report is on screen, and a disabled button
        // leaves the keyboard's reach, so the reason has to be announced, not just readable.
        <StyledAgentChatSharedReportNote id={expiredReasonId} role='status'>{expiredReason}</StyledAgentChatSharedReportNote>
      )}
    </>
  );
};

/**
 * Renders a single agent-chat message's body based on its `type`. Shared
 * between the drawer/embedded AgentChat and the full-width chat — each
 * passes in its own `Confirmation` component (background/border differ),
 * while `ConfirmationOption`/`OptionButton` are visually identical across
 * both, so they're shared directly rather than threaded through as props.
 */
export const AgentChatMessageContent = ({
  message,
  idx,
  allMessages,
  parseMessage,
  onSend,
  selectedOptions,
  onToggleOption,
  onSubmitOptions,
  Confirmation,
}) => {
  const {
    attachment, id, file, message: text, type = 'text', options, role, contentType, reportHtml, exports: reportExports, reportId, reportParams,
  } = message;

  // ⚠ BEFORE EVERY OTHER BRANCH. A report answers the question on its own — running it through the
  // markdown path would strip it to text, and through `parseMessage`'s table splitter would tear a
  // rendered table out of its own layout. `content_type` is the only thing that decides this; an
  // absent or unknown value falls through to markdown, which is what existing clients already do.
  if (contentType === CONTENT_TYPE.HTML && reportHtml) {
    return <AgentChatReport html={reportHtml} reportExports={reportExports} reportId={reportId} reportParams={reportParams} onSend={onSend} />;
  }

  // Error message
  if (type === 'error') {
    return <Alert type='error' showIcon message={text || 'An error occurred.'} />;
  }
  // File attachment
  if (type === 'file') {
    return <Attachments.FileCard key={id} item={file} />;
  }
  // Only display options if this is the latest message
  if (role === 'assistant' && idx !== allMessages.length - 1 && options) {
    return (
      <Confirmation>
        <div className='confirmation-message'>{renderParsedMessage(parseMessage({text, attachment}), attachment)}</div>
      </Confirmation>
    );
  }
  // Confirmation with options (only if latest message)
  if (type === 'options' && options) {
    return (
      <AgentChatOptions
        message={text}
        options={options}
        attachment={attachment}
        parseMessage={parseMessage}
        onSend={onSend}
        selectedOptions={selectedOptions}
        onToggleOption={onToggleOption}
        onSubmitOptions={onSubmitOptions}
        Confirmation={Confirmation}
      />
    );
  }
  if (typeof text === 'string') {
    return <div>{renderParsedMessage(parseMessage({text, attachment}), attachment)}</div>;
  }
  // Fallback for unknown types or missing message
  return null;
};
